/**
 * Indicador de salud de la API.
 *
 * Pega a `GET /api/v1/health/ready`, que es **publico** (esta en la lista
 * blanca de `src/api/deps.py`). Por eso funciona incluso sin sesion, y sirve
 * para distinguir dos cosas que desde el navegador se parecen mucho: "la API
 * esta caida" y "mi token no sirve".
 *
 * `degraded` (Redis caido) se muestra distinto de `not_ready` a proposito: el
 * backend documenta que con Redis caido la API igual sirve, mas lenta. Pintar
 * las dos de rojo entrenaria a ignorar el indicador.
 */

import { getReadiness, type ReadinessResponse } from "../api/endpoints";
import { useAuth } from "../auth/AuthProvider";
import { useAsync } from "./useAsync";

const LABELS: Record<ReadinessResponse["status"], string> = {
  ready: "API OK",
  degraded: "API degradada",
  not_ready: "API no lista",
};

export function ApiStatus() {
  const { api } = useAuth();
  const { state } = useAsync<ReadinessResponse>((signal) => getReadiness(api, { signal }), [
    api,
  ]);

  if (state.kind === "loading" || state.kind === "idle") {
    return <span className="api-status api-status--unknown">API ...</span>;
  }

  if (state.kind === "error") {
    return (
      <span className="api-status api-status--not_ready" title="No se pudo consultar">
        API sin respuesta
      </span>
    );
  }

  const failing = state.data.checks
    .filter((check) => check.status !== "ok")
    .map((check) => check.code ?? check.name)
    .join(", ");

  return (
    <span
      className={`api-status api-status--${state.data.status}`}
      title={failing ? `Fallando: ${failing}` : `v${state.data.version}`}
    >
      {LABELS[state.data.status]}
    </span>
  );
}
